import React, { useState } from 'react';
import { 
    Box, TextField, Button, Typography, Paper, CircularProgress, Alert, 
    Stack, LinearProgress, IconButton, InputAdornment, Tooltip 
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { authService } from '../api';

const ResetPassword = () => {
    const { t } = useTranslation();
    const { uid, token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const getPasswordStrength = (password) => {
        if (!password) return 0;
        let strength = 0;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        return strength;
    };

    const getStrengthLabel = (strength) => {
        switch(strength) {
            case 1: return t('strength_very_weak');
            case 2: return t('strength_weak');
            case 3: return t('strength_medium');
            case 4: return t('strength_strong');
            default: return '';
        }
    };

    const getStrengthColor = (strength) => {
        if (strength <= 2) return 'error';
        if (strength === 3) return 'warning';
        return 'success';
    };

    const handleGeneratePassword = () => {
        const length = 14;
        const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lower = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const symbols = "!@#$%^&*()-_=+";
        const allChars = upper + lower + numbers + symbols;

        let generated = "";
        // Aseguramos al menos uno de cada tipo requerido
        generated += upper[Math.floor(Math.random() * upper.length)];
        generated += lower[Math.floor(Math.random() * lower.length)];
        generated += numbers[Math.floor(Math.random() * numbers.length)];
        generated += symbols[Math.floor(Math.random() * symbols.length)];

        for (let i = 0; i < length - 4; i++) {
            generated += allChars[Math.floor(Math.random() * allChars.length)];
        }

        const shuffled = generated.split('').sort(() => 0.5 - Math.random()).join('');
        setPassword(shuffled);
        setConfirmPassword(shuffled);
        setShowPassword(true);
        setShowConfirmPassword(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setMessage({ text: 'Las contraseñas no coinciden.', type: 'error' });
            return;
        }

        if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
            setMessage({ 
                text: "La contraseña debe tener al menos una mayúscula y un número.", 
                type: 'error' 
            });
            return;
        }

        setLoading(true);
        try {
            await authService.confirmPasswordReset(uid, token, password);
            setMessage({ text: t('reset_password_success'), type: 'success' });
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setMessage({ text: t('reset_password_error'), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 112px)' }}>
            <Paper elevation={6} sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, maxWidth: 450, width: '100%', textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>{t('reset_password_title')}</Typography>
                
                {message.text && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}
                
                <form onSubmit={handleSubmit}>
                    <TextField
                        label={t('login_password')}
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {password && (
                        <Box sx={{ mb: 2, textAlign: 'left' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="caption">{t('password_strength')}</Typography>
                                    <Typography variant="caption" fontWeight="bold" color={`${getStrengthColor(getPasswordStrength(password))}.main`}>
                                        {getStrengthLabel(getPasswordStrength(password))}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={handleGeneratePassword}>
                                    {t('reset_password_generate')}
                                </Typography>
                            </Stack>
                            <LinearProgress 
                                variant="determinate" 
                                value={(getPasswordStrength(password) / 4) * 100} 
                                color={getStrengthColor(getPasswordStrength(password))}
                                sx={{ height: 6, borderRadius: 5 }}
                            />
                        </Box>
                    )}

                    {!password && (
                        <Box sx={{ mb: 1, textAlign: 'right' }}>
                            <Button 
                                size="small" 
                                startIcon={<AutoFixHighIcon />} 
                                onClick={handleGeneratePassword}
                            >
                                {t('reset_password_generate')}
                            </Button>
                        </Box>
                    )}

                    <TextField
                        label="Confirmar Contraseña"
                        type={showConfirmPassword ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{ mt: 3 }}
                        size="large"
                        disabled={loading || message.type === 'success'}
                    >
                        {loading ? <CircularProgress size={24} /> : t('reset_password_btn')}
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default ResetPassword;